import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type ProgressTrackerStep = 'in_queue' | 'registering' | 'active' | 'failed';

type CircleState = 'done' | 'active-purple' | 'active-blue' | 'upcoming' | 'failed';

function Circle({ state }: { state: CircleState }) {
  if (state === 'done') {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ecfdf5]">
        <i className="ri-check-double-line text-[16px] text-[#059669]" />
      </div>
    );
  }
  if (state === 'active-purple') {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#5625b0] bg-[#ede9fd]">
        <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-[#5625b0]" />
      </div>
    );
  }
  if (state === 'active-blue') {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#2200ce] bg-[#e3f2fd]">
        <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-[#2200ce]" />
      </div>
    );
  }
  if (state === 'failed') {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ffeded]">
        <i className="ri-alert-line text-[16px] text-red-500" />
      </div>
    );
  }
  return <div className="h-8 w-8 shrink-0 rounded-full border border-[rgba(17,24,39,0.4)]" />;
}

function Label({ children, dim }: { children: ReactNode; dim: boolean }) {
  return (
    <div
      className={`w-[70px] text-center text-[10px] font-semibold leading-[12px] ${
        dim ? 'text-[rgba(17,24,39,0.3)]' : 'text-[rgba(17, 24, 39, 0.80)]'
      }`}
    >
      {children}
    </div>
  );
}

function Line({ done }: { done?: boolean } = {}) {
  return <div className={`mt-4 h-px flex-1 ${done ? 'bg-[#059669]' : 'bg-[rgba(17,24,39,0.15)]'}`} />;
}

export default function ProgressTracker({ step }: { step: ProgressTrackerStep }) {
  const { t } = useTranslation();

  const circle2: CircleState = step === 'in_queue' ? 'active-purple' : 'done';
  const circle3: CircleState = step === 'in_queue' ? 'upcoming' : step === 'registering' ? 'active-blue' : 'done';
  const circle4: CircleState = step === 'failed' ? 'failed' : step === 'active' ? 'done' : 'upcoming';

  return (
    <div className="mx-auto flex max-w-[449px] items-start justify-between">
      <div className="flex flex-col items-center gap-1.5">
        <Circle state="done" />
        <Label dim={false}>{t('payment.tracker.paymentReceived')}</Label> 
      </div>
      <Line done />
      <div className="flex flex-col items-center gap-1.5">
        <Circle state={circle2} />
        <Label dim={false}>{t('payment.tracker.inQueue')}</Label>
      </div>
      <Line />
      <div className="flex flex-col items-center gap-1.5">
        <Circle state={circle3} />
        <Label dim={circle3 === 'upcoming'}>{t('payment.tracker.registering')}</Label>
      </div>
      <Line />
      <div className="flex flex-col items-center gap-1.5">
        <Circle state={circle4} />
        <Label dim={circle4 === 'upcoming'}>
          {step === 'failed' ? t('payment.tracker.failed') : t('payment.tracker.active')}
        </Label>
      </div>
    </div>
  );
}
